import React, { Component, FormEvent } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { logger } from "@formant/core/common/logger";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { IValidation } from "@formant/core/model/IValidation";
import { Uuid } from "@formant/core/model/Uuid";
import { InputElement } from "@formant/ui/component/common/Field";
import { TextField } from "@formant/ui/component/common/TextField";

import { track } from "../analytics";
import { currentLegal } from "../legal";
import {
    authStore,
    organizationStore,
    routerStore,
    userStore,
} from "../stores";
import { Buttons } from "./common/Buttons";
import { Checkbox } from "./common/Checkbox";
import { Form } from "./common/Form";
import { Heading2 } from "./common/Heading2";
import { Modal } from "./common/Modal";
import { PasswordField } from "./common/PasswordField";
import { StandardButton } from "./common/StandardButton";
import { TextButton } from "./common/TextButton";
import { PasswordStrength } from "./PasswordStrength";
import * as styles from "./ResetPassword.scss";

export interface IResetPasswordProps {
    userId: Uuid;
    email: string;
    session: string;
}

@observer
export class ResetPassword extends Component<IResetPasswordProps> {
    @observable
    private password: string = "";
    @observable
    private validation?: IValidation;
    @observable
    private loading: boolean = false;
    @observable
    private accepted: boolean = false;

    public componentDidMount() {
        track("Received self serve Sign Up Email");
    }

    public render() {
        const { email } = this.props;
        const { accepted } = this;
        const { routeTo } = routerStore;

        return (
            <Modal className={styles["reset-password"]} small={true}>
                <Heading2>We're excited you're here</Heading2>
                <Form onSubmit={this.onSubmit} onLoading={this.onLoading}>
                    <TextField
                        label="Email"
                        isEmail={true}
                        value={email}
                        autoComplete="username"
                        readOnly={true}
                        className={styles.hidden}
                        data-testid="email"
                    />
                    <PasswordField
                        data-testid="password"
                        label="Password"
                        placeholder="Create password"
                        name="password"
                        value={this.password}
                        onChange={this.onChangePassword}
                        autoComplete="new-password"
                        className={styles.field}
                        small={true}
                        invalid={
                            this.validation && !!this.validation.newPassword
                        }
                        errorMessage={this.validation}
                    />
                    <PasswordStrength password={this.password} />
                    <label className={styles.accept}>
                        <Checkbox
                            data-testid="terms-and-conditions-checkbox"
                            checked={accepted}
                            onChange={this.onChangeAccepted}
                        />
                        <div
                            className={styles.agreement}
                            data-testid="terms-and-conditions"
                        >
                            By checking this box and using Formant, you are
                            agreeing to our{" "}
                            <TextButton to={routeTo.terms()} target="_blank">
                                Terms of Service
                            </TextButton>{" "}
                            and{" "}
                            <TextButton
                                to={routeTo.privacyPolicy()}
                                target="_blank"
                            >
                                Privacy Policy
                            </TextButton>
                            . If you do not agree, you cannot use Formant.
                        </div>
                    </label>
                    <Buttons className={styles.buttons}>
                        <StandardButton
                            data-testid="get-started-button"
                            className={styles.submit}
                            primary={true}
                            large={true}
                            loading={this.loading}
                            disabled={this.password.length < 8 || !accepted}
                            arrow={!(this.password.length < 8 || !accepted)}
                        >
                            Get Started
                        </StandardButton>
                    </Buttons>
                </Form>
            </Modal>
        );
    }

    private onChangeAccepted = () => {
        this.accepted = !this.accepted;
    };

    private onChangePassword = (event: FormEvent<InputElement>) => {
        this.password = event.currentTarget.value;
        this.validation = undefined;
    };

    private onSubmit = async () => {
        const { userId, session, email } = this.props;
        this.validation = undefined;

        try {
            const legal = currentLegal({ organizationStore, userStore });

            if (!legal) {
                return;
            }
            await authStore.respondToNewPasswordRequiredChallenge(
                {
                    userId,
                    session,
                    newPassword: this.password,
                },
                legal.name,
            );
            track("Password created");
        } catch (error) {
            const responseError = narrowError(error, ResponseError);
            if (responseError.statusCode === 401) {
                logger.warn("Reset password failure", { email });
                this.validation = {
                    "": [
                        "There was an error with password reset. Follow the link in the invitation email to start again.",
                    ],
                };
            } else if (responseError.statusCode === 422) {
                this.validation = responseError.validationErrors;
            } else {
                throw error;
            }
        }

        return;
    };

    private onLoading = (loading: boolean) => {
        this.loading = loading;
    };
}
